import React, { Component } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { pathByRouteName } from '../../util/routes';

import { Modal } from '../../components';

import EmailReminder from './EmailReminder';
import css from './ModalMissingInformation.module.css';
import DateOfBirthRequired from './DateOfBirthRequired';
import MissingInfoReminder from './MissingInfoReminder';

export const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  // 'SignupPage',
  'ConfirmPage',
  // 'EditListingPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
  'ProfileSettingsPage',
  'LandingPage',
  'TermsOfServicePage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const DOB_REQUIRED = 'DOB_REQUIRED';
const COMPLETE_PROFILE_REQUIRED = 'COMPLETE_PROFILE_REQUIRED'

class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
    };
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
  }

  componentDidUpdate() {
    const { currentUser, currentUserHasListings, currentUserHasOrders, location, config } = this.props;
    const user = ensureCurrentUser(currentUser);
    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location,
      config
    );
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation,
    config
  ) {
    const routes = this.props.routeConfiguration;
    const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map(page =>
      pathByRouteName(page, routes)
    );

    // Is the current page whitelisted?
    const isPageWhitelisted = whitelistedPaths.includes(newLocation.pathname) || newLocation.pathname.includes("/signup/");

    // Track if path changes inside Page level component
    const pathChanged = newLocation.pathname !== this.props.location.pathname;
    const notRemindedYet =
      !this.state.showMissingInformationReminder && !this.state.hasSeenMissingInformationReminder;

    // Is the reminder already shown on current page
    const showOnPathChange = notRemindedYet || pathChanged;

    if (!isPageWhitelisted && showOnPathChange) {
      // Emails are sent when order is initiated
      // Customer is likely to get email soon when she books something
      // Provider email should work - she should get an email when someone books a listing
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
      const emailVerificationNeeded = emailUnverified;

      const {
        birthDate = undefined,
        birthMonth = undefined,
        birthYear = undefined,
        dob = undefined,
        telephone_contact = undefined,
        country_of_business = undefined
      } = currentUser?.attributes?.profile?.privateData || {}
      const dobRequiredNeeded = !!currentUser.id && !(birthDate && birthMonth && birthYear && dob);

      // const { asasets_of_focus, member_category, role, companyName } = currentUser?.attributes?.profile.publicData || {}
      const userFields = config.user?.userFields || [];
      const publicData = currentUser?.attributes?.profile?.publicData || {};

      const requiredFields = userFields
        .filter(field => field?.saveConfig?.isRequired)
        .map(field => field.key);

      const missingFields = requiredFields.filter(
        key => !publicData[key] || publicData[key]?.length === 0
      );

      // Show reminder
      if (emailVerificationNeeded) {
        this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      } else if (dobRequiredNeeded) {
        this.setState({ showMissingInformationReminder: DOB_REQUIRED });
      }
      else if (missingFields.length > 0) {
        // this.setState({ showMissingInformationReminder: COMPLETE_PROFILE_REQUIRED });
      }
    }
  }

  render() {
    const {
      rootClassName,
      className,
      containerClassName,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const classes = classNames(rootClassName || css.root, className);

    let content = null;

    const currentUserLoaded = user && user.id;
    if (currentUserLoaded) {
      if (this.state.showMissingInformationReminder === EMAIL_VERIFICATION) {
        content = (
          <EmailReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      } else if (this.state.showMissingInformationReminder === DOB_REQUIRED) {
        content = <DateOfBirthRequired className={classes} />;
      }
      else if (this.state.showMissingInformationReminder === COMPLETE_PROFILE_REQUIRED) {
        content = (
          <MissingInfoReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      }
    }
    const closeButtonMessage = (
      <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
    );

    return content ? (
      <Modal
        id="MissingInformationReminder"
        containerClassName={containerClassName}
        isOpen={true}
        onClose={() => {
          this.setState({
            showMissingInformationReminder: null,
            hasSeenMissingInformationReminder: true,
          });
        }}
        hideCloseBtn={this.state.showMissingInformationReminder === DOB_REQUIRED ||
          this.state.showMissingInformationReminder === COMPLETE_PROFILE_REQUIRED ||
          this.state.showMissingInformationReminder === EMAIL_VERIFICATION}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={closeButtonMessage}
      >
        {content}
      </Modal>
    ) : null;

  }
}

ModalMissingInformation.defaultProps = {
  className: null,
  rootClassName: null,
  currentUser: null,
};

ModalMissingInformation.propTypes = {
  id: string.isRequired,
  className: string,
  rootClassName: string,
  containerClassName: string,

  currentUser: propTypes.currentUser,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailError: propTypes.error,
  sendVerificationEmailInProgress: bool.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
